import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Table, Button, Tag, message } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import PromptTemplateStore from '../../stores/PromptTemplatesStore';
import { Utils } from '../../../common/services/Utils';
import { EditPromptTemplateStore } from '../../stores';

interface PromptTemplateVersion {
    id: string;
    version: number;
    isDraft: boolean;
    createdBy: string;
    createDate: string;
    title: string;
}

interface Props {
    templateId: string;
    store: PromptTemplateStore;
    editStore: EditPromptTemplateStore;
}

const PromptTemplateVersionsTab: React.FC<Props> = ({ templateId, store, editStore }) => {
    const [versions, setVersions] = React.useState<PromptTemplateVersion[]>([]);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (store.currentProject?.id) {
            loadVersions();
        }
    }, [templateId, store.currentProject?.id]);

    const loadVersions = async () => {
        setLoading(true);
        try {
            const result = await store.getPromptTemplateVersions(templateId);
            setVersions(result);
        } catch (error) {
            console.error('Failed to load versions:', error);
            message.error('Failed to load versions');
        } finally {
            setLoading(false);
        }
    };

    const handleSwitchVersion = async (id: string) => {
        try {
            await store.switchPromptTemplateVersion(id);
            editStore.initializeWithTemplate(templateId);
            loadVersions();
        } catch (error) {
            console.error('Failed to switch version:', error);
        }
    };

    // Get the current active version from the selected template
    const selectedTemplate = editStore.selectedTemplate;
    const currentActiveVersion = selectedTemplate?.currentVersion;
    const templateIsDraft = selectedTemplate?.isDraft;

    // Get draft versions to identify the oldest and most recent ones
    const draftVersions = versions
        .filter(v => v.isDraft)
        .sort((a, b) => new Date(a.createDate).getTime() - new Date(b.createDate).getTime());
    const oldestDraftId = draftVersions.length > 0 ? draftVersions[0].id : null;
    const mostRecentDraftId = draftVersions.length > 0 ? draftVersions[draftVersions.length - 1].id : null;

    const isActiveVersion = (record: PromptTemplateVersion) => {
        if (record.isDraft) {
            // For drafts: the most recent draft is active if the template is in draft mode
            return templateIsDraft && record.id === mostRecentDraftId;
        } else {
            // For published versions: match by version number and template must not be in draft mode
            return !templateIsDraft && record.version === currentActiveVersion;
        }
    };

    const columns: ColumnsType<PromptTemplateVersion> = [
        {
            title: 'Version',
            dataIndex: 'version',
            key: 'version',
            width: 150,
            render: (version: number, record: PromptTemplateVersion) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <strong>{record.isDraft ? 'Draft' : `v${version}`}</strong>
                    {isActiveVersion(record) && (
                        <Tag color="green" icon={<CheckCircleFilled />}>
                            ACTIVE
                        </Tag>
                    )}
                </div>
            )
        },
        {
            title: 'Status',
            dataIndex: 'isDraft',
            key: 'isDraft',
            width: 120,
            render: (isDraft: boolean) => (
                <Tag color={isDraft ? 'orange' : 'green'}>{isDraft ? 'Draft' : 'Published'}</Tag>
            )
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            ellipsis: true
        },
        {
            title: 'Created By',
            dataIndex: 'createdBy',
            key: 'createdBy',
            width: 150,
            render: (createdBy: string) => store.getUserNameById(createdBy)
        },
        {
            title: 'Created Date',
            dataIndex: 'createDate',
            key: 'createDate',
            width: 180,
            render: (date: string) => <span>{Utils.formatDateStringShort(date, true)}</span>
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            render: (_: any, record: PromptTemplateVersion) => {
                // Don't show any button for active version
                if (isActiveVersion(record)) {
                    return null;
                }

                const isOldestDraft = record.isDraft && record.id === oldestDraftId;
                const buttonText = isOldestDraft ? 'Revert' : 'Activate';

                return <Button onClick={() => handleSwitchVersion(record.id)}>{buttonText}</Button>;
            }
        }
    ];

    return (
        <div style={{ marginTop: 20 }}>
            <Table
                columns={columns}
                dataSource={versions}
                loading={loading}
                rowKey="id"
                pagination={{ pageSize: 10 }}
            />
        </div>
    );
};

export default observer(PromptTemplateVersionsTab);
